import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { ManualStatus, OccupiedDurationSeconds } from '@ixicare/resources';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { useDutyState } from '~hooks/useDutyState';
import DutyControl, { DutyModeProps } from './DutyControl';

export interface DutyControlContainerProps {
  className?: string;
}

const reportDutyError = (error: unknown) => console.error('Duty change failed:', error);

/** The duty control wired to the stores. Shared so every place that offers it
 *  offers the same one — the top bar, and the read-only explainer where it
 *  doubles as the way out of view-only mode. */
const DutyControlContainer: React.FC<DutyControlContainerProps> = observer(function DutyControlContainer({
  className,
}) {
  const { presence, sites } = useStores();
  const navigate = useNavigate();
  const {
    hasDutyAxis,
    isMultiSite,
    isDutyResolved,
    isReadOnly,
    isOnDuty,
    isViewingDutySite,
    canStopDuty,
    canChangeGroup,
    dutySiteUuid,
    dutySiteName,
    dutyGroupId,
    dutyGroupName,
    openCount,
    viewedSiteName,
    groupOptions,
    isUnsegmentedSite,
    activate,
    deactivate,
  } = useDutyState();

  // Clicking the count is a jump to the work itself, so it lands on the alarm
  // list of the duty site — distinct from the bar's "go to <site>" button,
  // which only moves your viewing context and leaves you on the same page.
  const goToDutyAlerts = React.useCallback(() => {
    if (!dutySiteUuid) return;
    sites.setSelectedSiteUuid(dutySiteUuid);
    navigate(`/alerts?site=${dutySiteUuid}`);
  }, [dutySiteUuid, navigate, sites]);

  // The pill names its site only when that isn't the one on screen — otherwise
  // it would repeat what the site switcher already says.
  const label = React.useMemo(() => {
    const parts = [!isViewingDutySite ? dutySiteName : undefined, dutyGroupName].filter(Boolean);
    return parts.length ? parts.join(' · ') : (dutySiteName ?? t('presence.state.online'));
  }, [isViewingDutySite, dutySiteName, dutyGroupName]);

  // Withheld until duty is known: rendering the off-duty CTA on an unresolved
  // guess flashes "Start handling alarms" at someone who already is. Without
  // it the control falls back to a plain presence pill, which is true either way.
  const duty: DutyModeProps | undefined =
    hasDutyAxis && isDutyResolved
      ? {
          isOnDuty,
          isMultiSite,
          canStopDuty,
          canChangeGroup,
          label,
          groupName: dutyGroupName,
          targetSiteName: viewedSiteName,
          dutySiteName,
          groupOptions,
          isUnsegmentedSite,
          selectedGroupId: dutyGroupId,
          // The count answers "work is waiting where you aren't looking", which
          // can't arise on one site — it would just sit there permanently,
          // duplicating the list below it.
          openCount: isMultiSite ? openCount : 0,
          onOpenCountClick: goToDutyAlerts,
          onPickGroup: (groupId: string) => void activate(groupId).catch(reportDutyError),
          onStartDuty: (groupId: string | null) => void activate(groupId).catch(reportDutyError),
          onStopDuty: () => void deactivate().catch(reportDutyError),
        }
      : undefined;

  return (
    <DutyControl
      currentStatus={presence.ownPresence}
      selectedDuration={presence.lastSelectedOccupiedDuration}
      onSelectOccupied={(seconds: OccupiedDurationSeconds) => void presence.setStatus(ManualStatus.Occupied, seconds)}
      onSelectUnavailable={() => void presence.setStatus(ManualStatus.Unavailable, null)}
      onClear={() => void presence.clearStatus()}
      duty={duty}
      readOnly={isReadOnly}
      className={className}
    />
  );
});

export default DutyControlContainer;
